import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Plus, Loader2, Power, Search, User as UserIcon,
  Shield, ShieldCheck, BarChart3, ChevronDown, X, Check,
  AlertTriangle, RefreshCw, Pencil, KeyRound, Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import AdminForm from '../components/forms/AdminForm';
import MatriceDesDroits from '../components/MatriceDesDroits';
import PiedTableau from '../components/PiedTableau';
import {
  annoncerAttention, annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees,
  Squelette, SqueletteTableau,
} from '../etats';

const ROLES = {
  SUPER_ADMIN: {
    label: 'Super Admin',
    badge: 'bg-[var(--accent)]/15 text-[var(--accent-texte)] border-[var(--accent)]/30',
    dot: 'bg-[var(--accent)]',
    Icon: ShieldCheck,
  },
  ADMIN: {
    label: 'Administrateur',
    badge: 'bg-[var(--nuit)]/10 text-[var(--text-bold)] border-[var(--border)]',
    dot: 'bg-[var(--info-fond)]',
    Icon: Shield,
  },
  ANALYSTE: {
    label: 'Analyste',
    badge: 'bg-[var(--nuit)]/10 text-[var(--text-bold)] border-[var(--border)]',
    dot: 'bg-[var(--info-fond)]',
    Icon: BarChart3,
  },
};

/* Le statut etait dit a deux endroits : « Actif / Suspendu » dans sa colonne,
   et « 1ere connexion requise » dans la colonne Email, a la place de l'adresse.
   Un compte suspendu ET incomplet n'affichait donc que l'un des deux, et la
   colonne Email cessait de contenir un e-mail sans prevenir.

   Un compte a UN etat. Il est calcule ici, une fois, et la colonne Email
   redevient une colonne d'e-mail. */
const STATUTS = {
  suspendu: { libelle: 'Suspendu', couleur: '#EF4444', fond: 'rgba(239,68,68,.10)', bord: 'rgba(239,68,68,.22)' },
  attente:  { libelle: 'Première connexion à faire', couleur: '#D97706', fond: 'rgba(217,119,6,.10)', bord: 'rgba(217,119,6,.22)' },
  actif:    { libelle: 'Actif', couleur: '#10B981', fond: 'rgba(16,185,129,.10)', bord: 'rgba(16,185,129,.22)' },
};

const estIncomplet = (a) => (a.email || '').endsWith('@incomplete.local') || !a.first_name;

/* L'ordre compte : suspendu prime sur tout le reste — un compte coupe n'est
   pas « en attente de premiere connexion », il est coupe. */
const statutDe = (a) => (!a.is_active ? 'suspendu' : estIncomplet(a) ? 'attente' : 'actif');

function BadgeStatut({ compte }) {
  const cfg = STATUTS[statutDe(compte)];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-controle text-[12px] font-semibold border whitespace-nowrap"
          style={{ color: cfg.couleur, background: cfg.fond, borderColor: cfg.bord }}>
      <span className="w-1.5 h-1.5 rounded-controle shrink-0" style={{ background: cfg.couleur }} />
      {cfg.libelle}
    </span>
  );
}

/* Quatre boutons pleine largeur par ligne faisaient de la derniere colonne un
   mur de commandes, plus large que l'identite qu'elle accompagnait. Une action
   principale reste visible, les trois autres passent derriere un menu : elles
   sont rares, et deux d'entre elles sont irreversibles. */
function MenuActions({ children, libelle }) {
  const [ouvert, setOuvert] = useState(false);
  const boite = useRef(null);

  useEffect(() => {
    if (!ouvert) return undefined;
    const auClic = (e) => { if (!boite.current?.contains(e.target)) setOuvert(false); };
    const auClavier = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  return (
    <div className="relative" ref={boite}>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label={libelle}
        style={{ minWidth: 36 }}
        className="act act-contour"
      >
        <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
      </button>
      {ouvert && (
        <div role="menu"
             onClick={() => setOuvert(false)}
             className="absolute right-0 mt-1 z-30 min-w-[210px] rounded-pastille shadow-carte bg-[var(--bg-card)] shadow-xl overflow-hidden py-1">
          {children}
        </div>
      )}
    </div>
  );
}

function EntreeMenu({ Icon, onClick, disabled, title, danger, busy, children }) {
  return (
    <button
      type="button" role="menuitem" onClick={onClick} disabled={disabled} title={title} aria-busy={busy || undefined}
      className={`w-full text-left px-4 py-2.5 text-[13px] flex items-center gap-2.5 transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${danger ? 'text-[var(--alerte)] hover:bg-[var(--alerte)]/10' : 'text-[var(--text-main)] hover:bg-[var(--bg-main)]'}`}
    >
      {busy
        ? <Loader2 className="w-4 h-4 shrink-0 animate-spin" aria-hidden="true" />
        : <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
      {children}
    </button>
  );
}

function RoleBadge({ role }) {
  const cfg = ROLES[role];
  if (!cfg) return <span className="text-[10px] text-[var(--text-muted)]">{role}</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-controle shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function RoleDropdown({ adminId, currentRole, currentUserId, canEdit, onChanged, onError }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Son propre role n'est pas modifiable (protection anti-verrouillage) ;
  // le changement de role est par ailleurs reserve aux super admins.
  if (adminId === currentUserId || !canEdit) return <RoleBadge role={currentRole} />;

  const handleSelect = async (newRole) => {
    if (newRole === currentRole) { setOpen(false); return; }
    setSaving(true);
    setOpen(false);
    try {
      await adminApi.updateAdminProfile(adminId, { role: newRole });
      annoncerSucces(`Rôle changé : ${ROLES[newRole]?.label || newRole}.`);
      onChanged();
    } catch (e) {
      onError(e?.message || 'Erreur lors du changement de rôle.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} disabled={saving} aria-busy={saving || undefined} className="flex items-center gap-1.5 group">
        <RoleBadge role={currentRole} />
        {saving
          ? <Loader2 className="w-3 h-3 text-[var(--text-muted)] animate-spin" />
          : <ChevronDown className={`w-3 h-3 text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
        }
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 bg-[var(--bg-card)] shadow-carte rounded-pastille py-1 min-w-[170px] overflow-hidden">
            {Object.entries(ROLES).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => handleSelect(key)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-[var(--bg-main)] transition-colors"
              >
                <cfg.Icon className={`w-3.5 h-3.5 ${key === currentRole ? 'text-[var(--accent-texte)]' : 'text-[var(--text-muted)]'}`} />
                <span className={`surtitre ${key === currentRole ? 'text-[var(--accent-texte)]' : 'text-[var(--text-main)]'}`}>
                  {cfg.label}
                </span>
                {key === currentRole && <Check className="w-3 h-3 text-[var(--accent-texte)] ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Bouton d'action compact ── */
/* Les quatre tons reprennent les variantes de la charte : un bouton
   d'action dans un tableau est un bouton comme un autre, en plus court
   (.act-sm), pas une forme a part. */
function ActionBtn({ onClick, disabled, title, tone = 'neutral', Icon, busy, children }) {
  const tones = {
    neutral: 'act-contour',
    danger:  'act-danger',
    ok:      'act-contour text-[var(--accent-texte)]',
    accent:  'act',
  };
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} title={title} aria-busy={busy || undefined}
      className={`act act-sm whitespace-nowrap ${tones[tone]}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export default function Admins({ user: currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const [erreurChargement, setErreurChargement] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  // Plus de `password` : le compte s'ouvre par un lien de première
  // connexion, c'est la personne qui choisira le sien.
  const [newAdmin, setNewAdmin] = useState({ username: '', email: '', role: 'ANALYSTE' });

  // Modifier / reinitialiser / supprimer
  const [editing, setEditing] = useState(null);      // compte en cours d'edition
  const [editForm, setEditForm] = useState({ username: '', email: '', first_name: '', last_name: '' });
  const [resettingId, setResettingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);    // compte a supprimer (confirmation)
  const [deleting, setDeleting] = useState(false);
  // L'erreur d'un formulaire reste dans le formulaire, sous les yeux.
  const [erreurEdition, setErreurEdition] = useState('');
  // Deux facettes du meme sujet : les comptes, et ce que leur role autorise.
  // Les separer en deux pages eloignerait la regle de son application.
  const [onglet, setOnglet] = useState('comptes');

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  /* Le retour d'une action passe par un toast. Un avertissement (compte
     créé mais lien non parti) reste jusqu'à ce qu'on le ferme : le compte
     est inaccessible tant qu'on n'a pas agi. */
  const notify = useCallback((type, text) => {
    if (type === 'ok') annoncerSucces(text);
    else if (type === 'warn') annoncerAttention(text, { duree: null });
    else annoncerErreur(text);
  }, []);

  const chargeRef = useRef(false);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAdmins();
      setAdmins(data || []);
      setErreurChargement(null);
      chargeRef.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue garde la liste affichée.
      if (chargeRef.current) notify('err', e?.message || 'La liste n’a pas pu être actualisée.');
      else setErreurChargement(e);
    }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => ({
    total:       admins.length,
    actifs:      admins.filter(a => statutDe(a) === 'actif').length,
    suspendus:   admins.filter(a => statutDe(a) === 'suspendu').length,
    aCompleter:  admins.filter(a => statutDe(a) === 'attente').length,
  }), [admins]);

  /* Les pastilles de role remplacent a la fois quatre cartes de comptage et la
     liste deroulante « Tous les roles ». Le chiffre et le filtre disaient la
     meme chose a deux endroits ; ils n'en font plus qu'un, et la ligne de
     filtres tient sur un telephone. */
  const pastillesRole = useMemo(() => ([
    { cle: 'all', libelle: 'Tous', n: admins.length },
    ...Object.entries(ROLES).map(([cle, cfg]) => ({
      cle, libelle: cfg.label, n: admins.filter(a => a.role === cle).length,
    })),
  ]), [admins]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return admins.filter(a => {
      const matchSearch = !s ||
        (a.username   || '').toLowerCase().includes(s) ||
        (a.first_name || '').toLowerCase().includes(s) ||
        (a.last_name  || '').toLowerCase().includes(s) ||
        (a.email      || '').toLowerCase().includes(s);
      const matchRole   = roleFilter   === 'all' || a.role === roleFilter;
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'actif'    &&  a.is_active) ||
        (statusFilter === 'suspendu' && !a.is_active) ||
        (statusFilter === 'incomplet' && estIncomplet(a));
      return matchSearch && matchRole && matchStatus;
    });
  }, [admins, search, roleFilter, statusFilter]);

  const handleToggle = async (id, current) => {
    setTogglingId(id);
    try {
      await adminApi.toggleAdminStatus(id, !current);
      loadData();
    } catch (e) { notify('err', e?.message || 'Erreur lors de la modification.'); }
    finally { setTogglingId(null); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newAdmin.username?.trim() || !newAdmin.email?.trim()) {
      notify('err', "Identifiant et adresse e-mail requis : le lien de "
                  + "première connexion part par courriel.");
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createAdmin(newAdmin);
      const destinataire = (newAdmin.email || '').trim();
      setShowAddModal(false);
      setNewAdmin({ username: '', email: '', role: 'ANALYSTE' });
      loadData();
      if (res?.email_sent) {
        notify('ok', `Compte créé. Le lien de première connexion a été envoyé à ${destinataire}.`);
      } else {
        // Le compte existe mais reste inaccessible tant que le lien n'est
        // pas parti : on le dit, plutôt que d'annoncer une réussite.
        notify('warn', `Compte créé, mais le lien n'a pas pu être envoyé`
          + `${res?.email_error ? ` (${res.email_error})` : ''}. `
          + `Utilisez « Renvoyer le lien » une fois l'adresse vérifiée.`);
      }
    } catch (e) {
      notify('err', e?.message || "L'identifiant est peut-être déjà utilisé.");
    } finally { setSaving(false); }
  };

  /* ── Modifier ── */
  const openEdit = (adm) => {
    setErreurEdition('');
    setEditing(adm);
    setEditForm({
      username:   adm.username   || '',
      email:      (adm.email || '').endsWith('@incomplete.local') ? '' : (adm.email || ''),
      first_name: adm.first_name || '',
      last_name:  adm.last_name  || '',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.username.trim()) { setErreurEdition("L'identifiant est obligatoire."); return; }
    setErreurEdition('');
    setSaving(true);
    try {
      await adminApi.updateAdminProfile(editing.id, {
        username:   editForm.username.trim(),
        email:      editForm.email.trim(),
        first_name: editForm.first_name.trim(),
        last_name:  editForm.last_name.trim(),
      });
      setEditing(null);
      loadData();
      notify('ok', 'Compte mis à jour.');
    } catch (e) {
      setErreurEdition(e?.message || "Modification impossible (identifiant déjà pris ?).");
    } finally { setSaving(false); }
  };

  /* ── Renvoyer un lien de connexion ─────────────────────────────────
     L'action ne génère plus de mot de passe : elle rend l'actuel
     inutilisable et envoie un lien pour en choisir un autre. Elle FERME
     donc l'accès en attendant — le bon effet pour un compte qu'on
     soupçonne compromis, mais qu'il faut annoncer avant de cliquer. */
  const handleReset = async (adm) => {
    const cible = adm.email && !adm.email.endsWith('@incomplete.local') ? adm.email : null;
    if (!cible) {
      notify('err', `« ${adm.username} » n'a pas d'adresse e-mail : renseignez-la `
                  + `avant d'envoyer un lien de connexion.`);
      return;
    }
    if (!await confirmer(
      `Envoyer un nouveau lien de connexion à « ${adm.username} » ?\n\n`
      + `Son mot de passe actuel cessera immédiatement de fonctionner. `
      + `Un lien part à ${cible} pour en choisir un nouveau.`
    )) return;
    setResettingId(adm.id);
    try {
      const res = await adminApi.resetAdminPassword(adm.id);
      if (res?.email_sent) notify('ok', `Lien de connexion envoyé à ${res.recipient}.`);
      else notify('warn', `Le mot de passe a été révoqué, mais le lien n'a pas pu être `
        + `envoyé${res?.email_error ? ` (${res.email_error})` : ''}. `
        + `Le compte reste inaccessible tant qu'il n'est pas reçu.`);
      loadData();
    } catch (e) { notify('err', e?.message || "L'envoi n'a pas abouti."); }
    finally { setResettingId(null); }
  };

  /* ── Supprimer ── */
  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteAdmin(toDelete.id);
      setToDelete(null);
      loadData();
      notify('ok', `Compte « ${toDelete.username} » supprimé.`);
    } catch (e) { notify('err', e?.message || 'Suppression impossible.'); }
    finally { setDeleting(false); }
  };

  const resetFilters = () => { setSearch(''); setRoleFilter('all'); setStatusFilter('all'); };
  const hasFilters = search || roleFilter !== 'all' || statusFilter !== 'all';

  if (loading && !charge) return (
    <div className="space-y-8">
      <div>
        <Squelette className="h-8 w-60 mb-3" />
        <Squelette className="h-3 w-96 max-w-full" />
      </div>
      <SqueletteTableau colonnes={4} />
    </div>
  );

  if (erreurChargement && !charge) return (
    <div className="space-y-8">
      <h1 className="titre-page">Rôles et accès</h1>
      <div className="bg-[var(--bg-card)] shadow-carte rounded-cadre"><EtatErreur erreur={erreurChargement} onReessayer={loadData} /></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in">

      {/* ── En-tête ── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="titre-page">Rôles et accès</h1>
          <p className="text-[13px] text-[var(--text-muted)] mt-1 max-w-2xl leading-relaxed">
            Les comptes de l'équipe et ce que leur rôle autorise. Créer un
            compte, changer un rôle, renvoyer un lien de connexion ou supprimer
            est réservé au super administrateur.
          </p>
        </div>
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            style={{ minHeight: 44 }}
            className="act shrink-0"
          >
            <Plus className="w-4 h-4" aria-hidden="true" /> Nouveau compte
          </button>
        )}
      </div>

      {/* ── Onglets ── */}
      <div className="flex gap-1 border-b border-[var(--border)]">
        {[
          { cle: 'comptes', libelle: `Comptes (${stats.total})` },
          { cle: 'permissions', libelle: 'Rôles et permissions' },
        ].map((o) => {
          const actif = onglet === o.cle;
          return (
            <button
              key={o.cle}
              type="button"
              onClick={() => setOnglet(o.cle)}
              style={{ minHeight: 44,
                       color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                       boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
              className="act act-tertiaire"
            >
              {o.libelle}
            </button>
          );
        })}
      </div>

      {onglet === 'permissions' ? <MatriceDesDroits /> : (
      <>

      {!isSuperAdmin && (
        <div className="flex items-start gap-3 bg-[var(--nuit)]/10 border border-[var(--border)] rounded-pastille px-5 py-4">
          <Shield className="w-4 h-4 text-[var(--text-bold)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--text-bold)] font-semibold">
            Modification des rôles, envoi d'un lien de connexion et suppression sont réservés aux <span className="font-semibold">super administrateurs</span>.
          </p>
        </div>
      )}

      {stats.aCompleter > 0 && (
        <div className="flex items-start gap-3 bg-[var(--attention)]/10 rounded-pastille px-5 py-4">
          <AlertTriangle className="w-4 h-4 text-[var(--attention)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--attention)] font-semibold">
            <span className="font-semibold">{stats.aCompleter} compte{stats.aCompleter > 1 ? 's' : ''}</span> en attente de complétion — l'admin doit se connecter et renseigner ses informations personnelles.
          </p>
        </div>
      )}

      {/* ── Filtres ── */}
      {/* La recherche prend la place qui reste, le filtre garde la sienne.
          `min-w-0` est indispensable : sans lui, l'element flex refuse de
          descendre sous la largeur de son contenu, et c'est la recherche
          -- pourtant en `flex-1` -- qui se retrouvait ecrasee a 62 px
          tandis que le select en occupait 829. */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher par nom, prénom ou email…"
            style={{ minHeight: 44 }}
            className="champ champ-loupe"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-bold)] transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filtre shrink-0 w-full md:w-auto md:min-w-[200px]"
        >
          <option value="all"       className="bg-[var(--bg-side)]">Tous les statuts</option>
          <option value="actif"     className="bg-[var(--bg-side)]">Actifs</option>
          <option value="suspendu"  className="bg-[var(--bg-side)]">Suspendus</option>
          <option value="incomplet" className="bg-[var(--bg-side)]">Profil incomplet</option>
        </select>

        {hasFilters && (
          <button
            onClick={resetFilters}
            style={{ minHeight: 44 }}
            className="act act-contour shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Réinitialiser
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Un choix exclusif : le contrôle segmenté de la charte, et non
            des puces dessinées à la main. Elles étaient figées à 36 px
            par un style en ligne, sous le seuil des 44 px au doigt — et
            rien ne les aurait fait suivre une évolution de la charte. */}
        <div className="segmente max-w-full overflow-x-auto" role="group"
             aria-label="Filtrer par rôle">
          {pastillesRole.map((p) => (
            <button
              key={p.cle}
              type="button"
              onClick={() => setRoleFilter(p.cle)}
              aria-pressed={roleFilter === p.cle}
              className="whitespace-nowrap shrink-0"
            >
              {p.libelle} <span className="opacity-60">{p.n}</span>
            </button>
          ))}
        </div>
        <p className="text-[12.5px] text-[var(--text-muted)]">
          {filtered.length} compte{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}
          {hasFilters && ` sur ${admins.length}`}
        </p>
      </div>

      {/* ── Tableau ── */}
      {filtered.length === 0 ? (
        <div className="bg-[var(--bg-card)] shadow-carte rounded-cadre">
          {admins.length === 0 ? (
            <EtatSansDonnees cas="vide" icone={UserIcon} titre="Aucun compte d'équipe"
              aide={isSuperAdmin ? 'Créez un compte pour donner accès au back-office.' : 'Un super administrateur peut créer les comptes de l’équipe.'}
              action={isSuperAdmin && (
                <button type="button" onClick={() => setShowAddModal(true)} className="act act-sm">
                  <Plus className="w-4 h-4" aria-hidden="true" /> Nouveau compte
                </button>
              )} />
          ) : search.trim() ? (
            <EtatSansDonnees cas="sansResultat" titre={`Aucun collaborateur pour « ${search.trim()} »`}
              action={<button type="button" onClick={() => setSearch('')} className="act act-sm act-contour">Effacer la recherche</button>} />
          ) : (
            <EtatSansDonnees cas="sansFiltre" titre="Aucun collaborateur ne correspond aux filtres"
              action={<button type="button" onClick={resetFilters} className="act act-sm act-contour">Réinitialiser</button>} />
          )}
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="tableau-cartes w-full text-left">
              <thead className="">
                <tr>
                  {['Collaborateur', 'Rôle', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {filtered.map((adm) => {
                  const isSelf      = adm.id === currentUser?.id;
                  const incomplete  = estIncomplet(adm);
                  const displayName = adm.first_name || adm.last_name
                    ? `${adm.first_name || ''} ${adm.last_name || ''}`.trim()
                    : null;

                  return (
                    <tr key={adm.id} className={`hover:bg-[var(--bg-main)] transition-all group ${!adm.is_active ? 'opacity-50' : ''}`}>

                      {/* Identite : le nom, l'identifiant et l'adresse se
                          lisent ensemble. Les separer en deux colonnes
                          obligeait a traverser le tableau pour savoir a qui
                          on ecrit. */}
                      <td data-libelle="Collaborateur" className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-pastille flex items-center justify-center font-semibold text-sm shrink-0 border
                            ${adm.is_active
                              ? 'bg-[var(--accent)]/10 text-[var(--accent-texte)] border-[var(--accent)]/20'
                              : 'bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)]'}`}>
                            {(displayName || adm.username || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[14px] font-semibold text-[var(--text-bold)] truncate">
                              {displayName || <span className="font-normal text-[var(--text-muted)]">Nom non renseigné</span>}
                              {isSelf && <span className="ml-2 text-[11px] font-semibold text-[var(--accent-texte)]">(vous)</span>}
                            </p>
                            <p className="text-[12px] text-[var(--text-muted)] truncate">
                              @{adm.username}
                              {!incomplete && adm.email ? <> · {adm.email}</> : null}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td data-libelle="Rôle" className="px-6 py-4">
                        <RoleDropdown
                          adminId={adm.id}
                          currentRole={adm.role}
                          currentUserId={currentUser?.id}
                          canEdit={isSuperAdmin}
                          onChanged={loadData}
                          onError={(m) => notify('err', m)}
                        />
                      </td>

                      <td data-libelle="Statut" className="px-6 py-4">
                        <BadgeStatut compte={adm} />
                      </td>

                      <td data-actions="" className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <ActionBtn
                            Icon={Pencil} tone="neutral" onClick={() => openEdit(adm)}
                            title="Modifier l'identifiant, l'e-mail et le nom"
                          >
                            Modifier
                          </ActionBtn>

                          <MenuActions libelle={`Autres actions sur ${adm.username}`}>
                            {isSuperAdmin && (
                              <EntreeMenu
                                Icon={KeyRound}
                                busy={resettingId === adm.id}
                                disabled={resettingId === adm.id}
                                onClick={() => handleReset(adm)}
                                title="Révoquer le mot de passe actuel et envoyer un lien pour en choisir un nouveau"
                              >
                                Renvoyer un lien de connexion
                              </EntreeMenu>
                            )}

                            <EntreeMenu
                              Icon={Power}
                              busy={togglingId === adm.id}
                              disabled={isSelf || togglingId === adm.id}
                              onClick={() => handleToggle(adm.id, adm.is_active)}
                              title={isSelf ? 'Impossible de modifier votre propre compte' : undefined}
                            >
                              {adm.is_active ? 'Suspendre le compte' : 'Réactiver le compte'}
                            </EntreeMenu>

                            {isSuperAdmin && (
                              <EntreeMenu
                                Icon={Trash2} danger
                                disabled={isSelf}
                                onClick={() => setToDelete(adm)}
                                title={isSelf ? 'Impossible de supprimer votre propre compte' : undefined}
                              >
                                Supprimer définitivement
                              </EntreeMenu>
                            )}
                          </MenuActions>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PiedTableau affiches={filtered.length} total={admins.length}
                       nom="comptes" />
        </div>
      )}

      </>
      )}

      {/* ── Modal création ── */}
      {showAddModal && (
        <AdminForm
          newAdmin={newAdmin}
          setNewAdmin={setNewAdmin}
          onSubmit={handleCreate}
          onClose={() => setShowAddModal(false)}
          saving={saving}
        />
      )}

      {/* ── Modal modification ── */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <form
            onSubmit={saveEdit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[var(--bg-card)] shadow-carte rounded-cadre p-8 space-y-5"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="titre-carte">
                  Modifier le <span className="text-[var(--accent-texte)]">compte</span>
                </h2>
                <p className="surtitre text-[var(--text-muted)] mt-2">
                  {editing.username}
                </p>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-[var(--text-muted)] hover:text-[var(--text-bold)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {erreurEdition && <div className="encart encart-alerte" role="alert">{erreurEdition}</div>}

            {[
              { k: 'username',   label: "Identifiant de connexion", required: true },
              { k: 'email',      label: 'Adresse e-mail', type: 'email' },
              { k: 'first_name', label: 'Prénom' },
              { k: 'last_name',  label: 'Nom' },
            ].map((f) => (
              <div key={f.k}>
                <label className="block surtitre text-[var(--text-muted)] mb-2">
                  {f.label}{f.required && ' *'}
                </label>
                <input
                  type={f.type || 'text'}
                  value={editForm[f.k]}
                  onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
                  className="champ font-bold"
                />
              </div>
            ))}

            <p className="text-[10px] text-[var(--text-muted)] font-semibold leading-relaxed">
              Le mot de passe ne se modifie pas ici : utilisez « Renvoyer un lien de connexion » pour que la personne en choisisse un nouveau.
            </p>

            <div className="flex gap-3 pt-1">
              <button
                type="submit" disabled={saving} aria-busy={saving || undefined}
                className="act flex-1"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
              <button
                type="button" onClick={() => setEditing(null)}
                className="act act-contour"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modal suppression ── */}
      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setToDelete(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--bg-card)] rounded-cadre shadow-2xl p-8 space-y-5 shadow-carte">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-pastille bg-[var(--alerte)]/10 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-[var(--alerte)]" />
              </div>
              <h2 className="titre-carte">
                Supprimer ce compte ?
              </h2>
            </div>
            <p className="text-sm text-[var(--text-main)] font-semibold leading-relaxed">
              Le compte <span className="font-semibold text-[var(--text-bold)]">{toDelete.username}</span>
              {toDelete.email ? <> ({toDelete.email})</> : null} sera <span className="text-[var(--alerte)] font-semibold">définitivement supprimé</span>.
              Cette action est irréversible.
            </p>
            <p className="text-[10px] text-[var(--text-muted)] font-semibold">
              Alternative : « Suspendre » désactive l'accès tout en conservant le compte et son historique.
            </p>
            <div className="flex gap-3">
              <button
                type="button" onClick={confirmDelete} disabled={deleting} aria-busy={deleting || undefined}
                className="act act-danger flex-1"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer définitivement
              </button>
              <button
                type="button" onClick={() => setToDelete(null)} disabled={deleting}
                className="act act-contour"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
